import { observer } from 'mobx-react';
import getWeatherOptions from '../data/store/store';
import { getCalendarConfiguration } from 'owa-calendar-configuration';
import MailboxCalendarConfiguration from 'owa-service/lib/contract/MailboxCalendarConfiguration';
import * as React from 'react';
import LocationCommonWell from 'owa-location-common-well/lib/components/LocationCommonWell';
import LocationCommonWellItemViewState from 'owa-location-common-well/lib/store/schema/LocationCommonWellItemViewState';
import { ControlIcons } from 'owa-control-icons';
import { ActionButton, PrimaryButton, IconButton } from 'office-ui-fabric-react/lib/Button';
import { getFallbackValueIfNull } from 'owa-options-core';
import WeatherOptionState from '../data/store/schema/WeatherOptionState';
import WeatherLocationRow from './WeatherLocationRow';
import requestCurrentPosition from 'owa-location-common-well/lib/utils/requestCurrentPosition';
import {
    findWeatherLocationFromLatLong,
    findWeatherLocationFromPostalAddress,
} from '../utils/findWeatherLocationUtils';
import WeatherLocationType from 'owa-service/lib/contract/WeatherLocationType';
import { setWeatherError } from '../data/actions/weatherMutatorActions';
import { addToWeatherList } from '../data/actions/weatherActions';
import { getLocationCommonWellItemViewStateFromLocationType } from '../utils/locationTypeConversionUtils';
import { LocationPersonaControlViewState } from 'owa-location-persona';
import strings from 'owa-strings/owa-options-strings';
import WeatherError from '../data/store/schema/WeatherError';
import { format } from 'owa-strings';
import { getFirstFocusable } from 'office-ui-fabric-react/lib/Utilities';

const styles = require('./WeatherLocations.scss');
const classNames = require('classnames/bind').bind(styles);

const MAX_WEATHER_LOCATIONS = 5;

export interface WeatherLocationsProps {
    disabled: boolean;
}

export interface WeatherLocationsState {
    isAdding: boolean;
    selectedItems: LocationCommonWellItemViewState[];
    locationToAdd: WeatherLocationType;
    locationPersonaToAdd: LocationPersonaControlViewState;
}

@observer
export default class WeatherLocations extends React.Component<
    WeatherLocationsProps,
    WeatherLocationsState
> {
    private locationWellContainer: HTMLDivElement;

    constructor(props: WeatherLocationsProps) {
        super(props);

        this.state = {
            isAdding: false,
            selectedItems: [],
            locationToAdd: null,
            locationPersonaToAdd: null,
        };
    }

    render() {
        const { disabled } = this.props;

        let optionStore: WeatherOptionState = getWeatherOptions();
        let calendarConfiguration: MailboxCalendarConfiguration = getCalendarConfiguration();

        let weatherLocations = getFallbackValueIfNull(
            optionStore.weatherLocations,
            calendarConfiguration.WeatherLocations
        );

        let disableAdding = disabled || weatherLocations.length >= MAX_WEATHER_LOCATIONS;

        return (
            <div className={styles.weatherLocationsWrapper}>
                <span>{strings.options_weather_addLocationSectionLabel}</span>
                <div
                    className={styles.addingLocationContainer}
                    ref={this.setLocationWellContainerRef}>
                    {!this.state.isAdding || disableAdding ? (
                        <>
                            <ActionButton
                                className={styles.addWeatherButton}
                                onClick={this.onAddButtonClicked}
                                text={strings.options_weather_addAnotherLocationLabel}
                                iconProps={{
                                    iconName: ControlIcons.Add,
                                    styles: {
                                        root: classNames(styles.icon),
                                    },
                                }}
                                disabled={disableAdding}
                            />
                            {optionStore.weatherErrorState &&
                                optionStore.weatherErrorState ===
                                    WeatherError.AddingExistingLocation && (
                                    <div className={styles.weatherAlreadyAddedWarning}>
                                        {format(
                                            strings.options_weather_AddingExistingLocationErrorText,
                                            optionStore.weatherErrorLocationName
                                        )}
                                    </div>
                                )}
                        </>
                    ) : (
                        <>
                            <LocationCommonWell
                                key="locationCommonWell"
                                containerClassName={styles.locationCommonWellContainer}
                                defaultSelectedItems={[]}
                                selectedItems={this.state.selectedItems}
                                draggableItemType={null}
                                dropViewState={null}
                                inputProps={{
                                    placeholder:
                                        strings.options_weather_addLocationWellPlaceholderText,
                                    'aria-label':
                                        strings.options_weather_addLocationWellPlaceholderText,
                                }}
                                onLocationsChanged={this.onLocationsChanged}
                                itemLimit={1}
                                isSmallSize={true}
                                maxItemWidth={190}
                                zeroQueryDisabled={true}
                                searchMode={'OnlyCities'}
                                disableUseThisLocationButton={true}
                            />
                            <IconButton
                                className={styles.myLocationButton}
                                iconProps={{
                                    iconName: ControlIcons.Poi,
                                    styles: { root: { fontSize: '26px' } },
                                }}
                                onClick={this.onMyLocationClicked}
                            />
                            <PrimaryButton
                                disabled={
                                    !this.state.locationToAdd && !this.state.locationPersonaToAdd
                                }
                                onClick={this.onConfirmButtonClicked}
                                text={strings.options_weather_confirmLocationButtonText}
                            />
                        </>
                    )}
                </div>
                {weatherLocations.map((location, index) => {
                    return (
                        <WeatherLocationRow
                            index={index}
                            rowTextValue={location.Name}
                            disabled={disabled || weatherLocations.length == 1}
                        />
                    );
                })}
            </div>
        );
    }

    public componentDidUpdate() {
        if (this.state.isAdding) {
            let firstFocusable = getFirstFocusable(
                this.locationWellContainer,
                this.locationWellContainer,
                true
            );
            if (firstFocusable && firstFocusable.focus) {
                firstFocusable.focus();
            }
        }
    }

    private setLocationWellContainerRef = (element: HTMLDivElement): void => {
        this.locationWellContainer = element;
    };

    private onConfirmButtonClicked = () => {
        setWeatherError(null);

        if (this.state.locationPersonaToAdd) {
            findWeatherLocationFromPostalAddress(
                this.state.locationPersonaToAdd.postalAddress
            ).then(resolvedLocation => {
                resolvedLocation && addToWeatherList(resolvedLocation);
            });
        } else {
            addToWeatherList(this.state.locationToAdd);
        }

        this.setState({
            isAdding: false,
            selectedItems: [],
            locationToAdd: null,
            locationPersonaToAdd: null,
        });
    };

    private onMyLocationClicked = () => {
        this.setState({
            selectedItems: [],
            locationToAdd: null,
            locationPersonaToAdd: null,
        });
        requestCurrentPosition(this.onRequestCurrentPositionSucceeded);
    };

    private onRequestCurrentPositionSucceeded = (position: Position) => {
        findWeatherLocationFromLatLong(position.coords.latitude, position.coords.longitude).then(
            resolvedLocation => {
                let locationItemViewState = getLocationCommonWellItemViewStateFromLocationType(
                    resolvedLocation
                );

                this.setState({
                    selectedItems: [locationItemViewState],
                    locationToAdd: resolvedLocation,
                });
            }
        );
    };

    private onLocationsChanged = (locations: LocationCommonWellItemViewState[]) => {
        if (locations.length > 0) {
            let location = locations[0];
            this.setState({
                selectedItems: [location],
                locationPersonaToAdd: location.persona,
            });
        } else {
            this.setState({
                selectedItems: [],
                locationPersonaToAdd: null,
                locationToAdd: null,
            });
        }
    };

    private onAddButtonClicked = () => {
        this.setState({
            isAdding: true,
            selectedItems: [],
        });
    };
}
